// email/studio.js - admin email & newsletter studio.
// Three areas inside the admin "Campaigns" subtab:
//   Compose   - pick segment + template (or free-write), preview, test, broadcast
//   Templates - full CRUD editing of reusable templates
//   History   - past campaigns with sent/total + unique-open rate
// All endpoints are admin-only (/api/admin/email/*). Sends are auto-logged in the
// Emails audit panel and carry unsubscribe + open tracking.

import { getUser } from '../auth.js';

function token() { return localStorage.getItem('gdbs_token') || ''; }

async function api(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  if (token()) headers['Authorization'] = 'Bearer ' + token();
  const res = await fetch(path, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${method} ${path}: ${res.status}`);
  return json;
}

function esc(s) {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;','\'':'&#39;' }[c]));
}

const VARS_HINT = 'Variables: <code>{{name}}</code> <code>{{email}}</code> <code>{{unsubscribe_url}}</code>';
let templates = [];
let segments = [];
let features = [];
let domains = []; // distinct `vertical` values from /pages/catalog.json - drives the per-domain newsletter selectors
let githubConfigured = false;
let area = 'compose';
let editingId = null; // template being edited (null = new)

function root() { return document.getElementById('email-studio'); }

// Domain (vertical) <option> list, shared by the compose + features selectors.
// A blank value means "all domains" - the {{#features}} block is not filtered to a vertical.
function domainOpts(sel) {
  return ['<option value="">All domains</option>']
    .concat(domains.map(d => `<option value="${esc(d)}"${sel === d ? ' selected' : ''}>${esc(d)}</option>`)).join('');
}

function subnav() {
  const tab = (id, label) =>
    `<button class="email-subtab btn btn-sm${area === id ? ' btn-primary' : ''}" data-area="${id}">${label}</button>`;
  return `<div style="display:flex;gap:8px;margin-bottom:16px;">
    ${tab('compose', 'Compose')}${tab('templates', 'Templates')}${tab('features', 'Features')}${tab('history', 'History')}</div>`;
}

// ---- Compose ----------------------------------------------------------------

function composeView() {
  const tplOpts = ['<option value="">- free compose -</option>']
    .concat(templates.map(t => `<option value="${t.id}">${esc(t.name)}</option>`)).join('');
  const segOpts = segments.map(s => `<option value="${s.id}">${esc(s.label)} (${s.count})</option>`).join('');
  return `
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:18px;">
    <div>
      <label class="email-lbl">Start from template</label>
      <select id="em-tpl" class="form-select" style="width:100%;padding:7px;margin-bottom:12px;">${tplOpts}</select>

      <label class="email-lbl">Audience segment</label>
      <select id="em-seg" class="form-select" style="width:100%;padding:7px;margin-bottom:12px;">${segOpts}</select>

      <label class="email-lbl">Domain (vertical)</label>
      <select id="em-domain" class="form-select" style="width:100%;padding:7px;">${domainOpts()}</select>
      <div style="font-size:11px;color:#64748b;margin:4px 0 12px;">A domain issue features only that vertical's shipped features in the <code>{{#features}}</code> block. Leave on "All domains" for a general issue.</div>

      <label class="email-lbl">Ad-hoc recipients (optional)</label>
      <textarea id="em-recipients" style="width:100%;height:64px;padding:8px;font-family:monospace;font-size:12px;" placeholder="jane@acme.com, sam@acme.com"></textarea>
      <div id="em-rec-count" style="font-size:11px;color:#64748b;margin:4px 0 12px;">Comma or newline separated. When filled, this overrides the audience segment above and sends to exactly these addresses.</div>

      <label class="email-lbl">Theme</label>
      <select id="em-theme" class="form-select" style="width:100%;padding:7px;margin-bottom:12px;">
        <option value="light">Light (recommended for newsletters)</option>
        <option value="dark">Dark</option>
      </select>

      <label class="email-lbl">Subject</label>
      <input id="em-subj" type="text" style="width:100%;padding:7px;margin-bottom:12px;" placeholder="Subject line">

      <label class="email-lbl">Body (HTML)</label>
      <textarea id="em-body" style="width:100%;height:230px;padding:8px;font-family:monospace;font-size:12px;" placeholder="<p>Hello {{name}},</p>"></textarea>
      <div style="font-size:11px;color:var(--text-dim);margin-top:4px;">${VARS_HINT} - the branded shell, unsubscribe footer and tracking pixel are added automatically.<br>Newsletter block: wrap an item layout in <code>{{#features}}</code> ... <code>{{/features}}</code> using <code>{{feature_name}}</code> <code>{{feature_desc}}</code> <code>{{feature_date}}</code> - it repeats for each feature shipped in the last month (logged under the Features tab).</div>

      <div style="margin-top:12px;display:flex;gap:10px;align-items:flex-end;flex-wrap:wrap;">
        <div>
          <label class="email-lbl">When</label>
          <select id="em-when" class="form-select" style="padding:7px;">
            <option value="now">Send now</option>
            <option value="once">Schedule once</option>
            <option value="recurring">Recurring newsletter</option>
          </select>
        </div>
        <div id="em-when-time" style="display:none;">
          <label class="email-lbl">Start (your local time)</label>
          <input id="em-datetime" type="datetime-local" style="padding:6px;">
        </div>
        <div id="em-when-rec" style="display:none;">
          <label class="email-lbl">Repeat</label>
          <select id="em-recurrence" class="form-select" style="padding:7px;">
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      <div style="display:flex;gap:8px;margin-top:14px;flex-wrap:wrap;">
        <button class="btn btn-sm" id="em-preview"><i class="fas fa-eye"></i> Preview</button>
        <button class="btn btn-sm" id="em-test"><i class="fas fa-paper-plane"></i> Send test to me</button>
        <button class="btn btn-sm btn-primary" id="em-send"><i class="fas fa-bullhorn"></i> Send campaign</button>
      </div>
      <div id="em-compose-status" style="font-size:12px;margin-top:10px;"></div>
    </div>
    <div>
      <label class="email-lbl">Preview</label>
      <iframe id="em-frame" style="width:100%;height:430px;border:1px solid var(--border,#2a2a4a);border-radius:6px;background:#fff;"></iframe>
    </div>
  </div>`;
}

function wireCompose() {
  const tpl = document.getElementById('em-tpl');
  tpl?.addEventListener('change', () => {
    const t = templates.find(x => x.id === tpl.value);
    if (t) {
      document.getElementById('em-subj').value = t.subject || '';
      document.getElementById('em-body').value = t.bodyHtml || '';
      document.getElementById('em-theme').value = t.theme || 'light';
    }
  });
  const when = document.getElementById('em-when');
  const sendBtn = document.getElementById('em-send');
  when?.addEventListener('change', () => {
    const v = when.value;
    document.getElementById('em-when-time').style.display = (v === 'once' || v === 'recurring') ? '' : 'none';
    document.getElementById('em-when-rec').style.display = (v === 'recurring') ? '' : 'none';
    sendBtn.innerHTML = v === 'now' ? '<i class="fas fa-bullhorn"></i> Send campaign'
      : v === 'once' ? '<i class="fas fa-clock"></i> Schedule send'
      : '<i class="fas fa-repeat"></i> Schedule newsletter';
  });
  const recBox = document.getElementById('em-recipients');
  const recCount = document.getElementById('em-rec-count');
  const updateRecCount = () => {
    if (!recCount) return;
    const n = parseRecipients().length;
    recCount.innerHTML = n
      ? `<span style="color:#475569;">Overrides the audience segment - will send to ${n} ad-hoc recipient${n === 1 ? '' : 's'}.</span>`
      : 'Comma or newline separated. When filled, this overrides the audience segment above and sends to exactly these addresses.';
  };
  recBox?.addEventListener('input', updateRecCount);
  document.getElementById('em-preview')?.addEventListener('click', doPreview);
  document.getElementById('em-test')?.addEventListener('click', doTest);
  document.getElementById('em-send')?.addEventListener('click', doSend);
}

function themeVal() { return document.getElementById('em-theme')?.value || 'light'; }
function domainVal() { return document.getElementById('em-domain')?.value || ''; }

// Parse the ad-hoc recipients textarea into a trimmed, deduped list of addresses.
// Split on commas, newlines and surrounding whitespace; keep only entries that look like an email.
function parseRecipients() {
  const raw = document.getElementById('em-recipients')?.value || '';
  const seen = new Set();
  const out = [];
  raw.split(/[\s,;]+/).forEach(part => {
    const e = part.trim();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return;
    const key = e.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(e);
  });
  return out;
}

async function doPreview() {
  const body = document.getElementById('em-body').value;
  try {
    const r = await api('POST', '/api/admin/email/preview', { bodyHtml: body, name: 'there', email: 'you@example.com', theme: themeVal(), domain: domainVal() });
    document.getElementById('em-frame').srcdoc = r.data.html;
  } catch (e) { setComposeStatus(e.message, true); }
}

async function doTest() {
  const subject = document.getElementById('em-subj').value.trim();
  const body = document.getElementById('em-body').value;
  if (!subject) return setComposeStatus('Subject is required.', true);
  setComposeStatus('Sending test...');
  try {
    const r = await api('POST', '/api/admin/email/test', { subject, bodyHtml: body, theme: themeVal() });
    setComposeStatus('Test sent to ' + esc(r.data.sentTo) + '.');
  } catch (e) { setComposeStatus(e.message, true); }
}

async function doSend() {
  const subject = document.getElementById('em-subj').value.trim();
  const body = document.getElementById('em-body').value;
  const seg = document.getElementById('em-seg');
  const segId = seg.value;
  const segLabel = seg.options[seg.selectedIndex].text;
  const segCount = (segments.find(s => s.id === segId) || {}).count || 0;
  const tplId = document.getElementById('em-tpl').value || null;
  const when = document.getElementById('em-when').value;
  if (!subject || !body.trim()) return setComposeStatus('Subject and body are required.', true);

  let scheduledAt = null, recurrence = null;
  if (when === 'once' || when === 'recurring') {
    const dtVal = document.getElementById('em-datetime').value;
    if (!dtVal) return setComposeStatus('Pick a start date/time.', true);
    const d = new Date(dtVal);
    if (isNaN(d)) return setComposeStatus('Invalid start date/time.', true);
    scheduledAt = d.toISOString(); // local -> UTC
  }
  if (when === 'recurring') recurrence = document.getElementById('em-recurrence').value;

  const domain = domainVal();
  // Ad-hoc recipients, when supplied, override the segment on the backend - reflect that in the audience wording.
  const recipients = parseRecipients();
  const audienceCount = recipients.length || segCount;
  const audienceLabel = recipients.length ? `${recipients.length} ad-hoc recipient(s) (overrides segment)` : `"${segLabel}"`;

  const localWhen = scheduledAt ? new Date(scheduledAt).toLocaleString() : '';
  const confirmMsg =
    when === 'now' ? `Send "${subject}" to ${audienceCount} recipient(s) in ${audienceLabel} now?\n\nThis sends real email and cannot be undone.`
    : when === 'once' ? `Schedule "${subject}" for ${localWhen} to ${audienceCount} recipient(s) in ${audienceLabel}?`
    : `Start a ${recurrence} newsletter "${subject}" beginning ${localWhen}, to ${audienceLabel}?\n\nIt sends every ${recurrence === 'weekly' ? 'week' : 'month'} until you cancel it.`;
  if (!confirm(confirmMsg)) return;

  setComposeStatus(when === 'now' ? 'Queuing campaign...' : 'Scheduling...');
  try {
    const campaign = { subject, bodyHtml: body, segment: segId, templateId: tplId, scheduledAt, recurrence, theme: themeVal(), domain };
    if (recipients.length) campaign.recipients = recipients; // non-empty -> backend ignores `segment` and sends to exactly these
    const r = await api('POST', '/api/admin/email/campaigns', campaign);
    if (r.warning) { setComposeStatus(r.warning, true); return; }
    setComposeStatus(
      when === 'now' ? 'Campaign queued for ' + r.data.totalRecipients + ' recipient(s).'
      : when === 'recurring' ? `${recurrence[0].toUpperCase() + recurrence.slice(1)} newsletter scheduled, first issue ${localWhen}.`
      : 'Campaign scheduled for ' + localWhen + '.');
    area = 'history'; render();
  } catch (e) { setComposeStatus(e.message, true); }
}

function setComposeStatus(msg, err) {
  const el = document.getElementById('em-compose-status');
  if (el) el.innerHTML = `<span style="color:${err ? '#ef4444' : '#10b981'};">${esc(msg)}</span>`;
}

// ---- Templates --------------------------------------------------------------

function templatesView() {
  const rows = templates.map(t => `
    <tr>
      <td>${esc(t.name)} ${t.isSystem ? '<span style="font-size:10px;color:#64748b;">(starter)</span>' : ''}</td>
      <td style="color:var(--text-dim);">${esc(t.subject)}</td>
      <td style="white-space:nowrap;">
        <button class="btn btn-sm" data-tpl-edit="${t.id}"><i class="fas fa-pen"></i></button>
        <button class="btn btn-sm" data-tpl-del="${t.id}" style="color:#ef4444;"><i class="fas fa-trash"></i></button>
      </td>
    </tr>`).join('');
  return `
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
    <h3 style="margin:0;">Templates</h3>
    <button class="btn btn-sm btn-primary" id="em-tpl-new"><i class="fas fa-plus"></i> New template</button>
  </div>
  <div class="admin-table-wrap"><table class="admin-table">
    <thead><tr><th>Name</th><th>Subject</th><th></th></tr></thead>
    <tbody>${rows || '<tr><td colspan="3" class="admin-empty">No templates yet.</td></tr>'}</tbody>
  </table></div>
  <div id="em-tpl-editor"></div>`;
}

function wireTemplates() {
  document.getElementById('em-tpl-new')?.addEventListener('click', () => { editingId = null; openTemplateEditor(); });
  root().querySelectorAll('[data-tpl-edit]').forEach(b =>
    b.addEventListener('click', () => { editingId = b.getAttribute('data-tpl-edit'); openTemplateEditor(); }));
  root().querySelectorAll('[data-tpl-del]').forEach(b =>
    b.addEventListener('click', async () => {
      if (!confirm('Delete this template?')) return;
      try { await api('DELETE', '/api/admin/email/templates/' + b.getAttribute('data-tpl-del')); await loadTemplates(); render(); }
      catch (e) { alert(e.message); }
    }));
}

function openTemplateEditor() {
  const t = editingId ? templates.find(x => x.id === editingId) : { name: '', description: '', subject: '', bodyHtml: '' };
  const host = document.getElementById('em-tpl-editor');
  host.innerHTML = `
    <div style="margin-top:16px;border-top:1px solid var(--border,#2a2a4a);padding-top:16px;">
      <h4 style="margin:0 0 10px;">${editingId ? 'Edit' : 'New'} template</h4>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
        <div><label class="email-lbl">Name</label><input id="te-name" type="text" style="width:100%;padding:7px;" value="${esc(t.name)}"></div>
        <div><label class="email-lbl">Subject</label><input id="te-subj" type="text" style="width:100%;padding:7px;" value="${esc(t.subject)}"></div>
      </div>
      <div style="display:grid;grid-template-columns:2fr 1fr;gap:10px;margin-bottom:10px;">
        <div><label class="email-lbl">Description</label><input id="te-desc" type="text" style="width:100%;padding:7px;" value="${esc(t.description)}"></div>
        <div><label class="email-lbl">Theme</label>
          <select id="te-theme" class="form-select" style="width:100%;padding:7px;">
            <option value="light"${(t.theme || 'light') === 'light' ? ' selected' : ''}>Light</option>
            <option value="dark"${t.theme === 'dark' ? ' selected' : ''}>Dark</option>
          </select>
        </div>
      </div>
      <label class="email-lbl">Body (HTML)</label>
      <textarea id="te-body" style="width:100%;height:200px;padding:8px;font-family:monospace;font-size:12px;">${esc(t.bodyHtml)}</textarea>
      <div style="font-size:11px;color:var(--text-dim);margin-top:4px;">${VARS_HINT}</div>
      <div style="display:flex;gap:8px;margin-top:12px;">
        <button class="btn btn-sm btn-primary" id="te-save"><i class="fas fa-save"></i> Save</button>
        <button class="btn btn-sm" id="te-cancel">Cancel</button>
        <span id="te-status" style="font-size:12px;align-self:center;"></span>
      </div>
    </div>`;
  document.getElementById('te-cancel').addEventListener('click', () => { host.innerHTML = ''; });
  document.getElementById('te-save').addEventListener('click', async () => {
    const dto = {
      id: editingId || undefined,
      name: document.getElementById('te-name').value.trim(),
      description: document.getElementById('te-desc').value.trim(),
      subject: document.getElementById('te-subj').value.trim(),
      bodyHtml: document.getElementById('te-body').value,
      theme: document.getElementById('te-theme').value,
    };
    if (!dto.name || !dto.subject) { document.getElementById('te-status').innerHTML = '<span style="color:#ef4444;">Name and subject required.</span>'; return; }
    try { await api('POST', '/api/admin/email/templates', dto); await loadTemplates(); render(); }
    catch (e) { document.getElementById('te-status').innerHTML = `<span style="color:#ef4444;">${esc(e.message)}</span>`; }
  });
}

// ---- Features (changelog that feeds the newsletter) -------------------------

function featuresView() {
  const curVer = (document.getElementById('gdbs-version')?.textContent || '').trim();
  const rows = features.map(f => {
    const isGh = f.source === 'github';
    const srcTag = isGh
      ? '<span style="font-size:10px;color:#8b5cf6;"><i class="fab fa-github"></i> auto</span>'
      : '<span style="font-size:10px;color:#64748b;">manual</span>';
    const del = isGh ? '' : `<button class="btn btn-sm" data-feat-del="${f.id}" style="color:#ef4444;"><i class="fas fa-trash"></i></button>`;
    return `<tr>
      <td>${esc(f.title)}</td>
      <td style="color:var(--text-dim);">${esc(f.description)}</td>
      <td style="white-space:nowrap;color:#64748b;">${f.domain ? esc(f.domain) : '<span style="color:#94a3b8;">All</span>'}</td>
      <td style="white-space:nowrap;">${esc(f.version || '')}</td>
      <td style="white-space:nowrap;">${esc((f.addedAt || '').slice(0, 10))}</td>
      <td>${srcTag}</td>
      <td>${del}</td>
    </tr>`;
  }).join('');
  const ghNote = githubConfigured
    ? 'Auto-synced from GitHub feat() commits (refreshes ~every 30 min). Add a one-off below for anything not captured in a commit.'
    : '<span style="color:#f59e0b;">GitHub token not set - showing manual features only. Add GitHub:Token to secrets to auto-pull feat() commits.</span>';
  return `
  <div style="margin-bottom:12px;">
    <div style="display:flex;justify-content:space-between;align-items:center;">
      <h3 style="margin:0 0 6px;">Shipped features</h3>
      <button class="btn btn-sm" id="feat-sync"><i class="fas fa-sync-alt"></i> Sync from GitHub</button>
    </div>
    <p style="font-size:12px;color:var(--text-dim);margin:0 0 10px;">${ghNote}</p>
    <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
      <div style="flex:2;min-width:150px;"><label class="email-lbl">Title</label><input id="feat-title" type="text" style="width:100%;padding:7px;"></div>
      <div style="flex:3;min-width:200px;"><label class="email-lbl">Description</label><input id="feat-desc" type="text" style="width:100%;padding:7px;"></div>
      <div style="min-width:180px;"><label class="email-lbl">Domain</label><select id="feat-domain" class="form-select" style="width:100%;padding:7px;">${domainOpts()}</select></div>
      <div style="width:84px;"><label class="email-lbl">Version</label><input id="feat-ver" type="text" style="width:100%;padding:7px;" value="${esc(curVer)}"></div>
      <button class="btn btn-sm btn-primary" id="feat-add"><i class="fas fa-plus"></i> Add manual</button>
    </div>
    <div id="feat-status" style="font-size:12px;margin-top:6px;"></div>
  </div>
  <div class="admin-table-wrap"><table class="admin-table">
    <thead><tr><th>Feature</th><th>Description</th><th>Domain</th><th>Version</th><th>Added</th><th>Source</th><th></th></tr></thead>
    <tbody>${rows || '<tr><td colspan="7" class="admin-empty">No features yet.</td></tr>'}</tbody>
  </table></div>`;
}

function wireFeatures() {
  document.getElementById('feat-sync')?.addEventListener('click', async () => {
    const s = document.getElementById('feat-status');
    if (s) s.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Syncing from GitHub...';
    try { await api('POST', '/api/admin/email/features/sync'); await loadFeatures(); render(); }
    catch (e) { if (s) s.innerHTML = `<span style="color:#ef4444;">${esc(e.message)}</span>`; }
  });
  document.getElementById('feat-add')?.addEventListener('click', async () => {
    const title = document.getElementById('feat-title').value.trim();
    const description = document.getElementById('feat-desc').value.trim();
    const version = document.getElementById('feat-ver').value.trim();
    const domain = document.getElementById('feat-domain')?.value || '';
    if (!title) { document.getElementById('feat-status').innerHTML = '<span style="color:#ef4444;">Title required.</span>'; return; }
    try { await api('POST', '/api/admin/email/features', { title, description, version, domain }); await loadFeatures(); render(); }
    catch (e) { document.getElementById('feat-status').innerHTML = `<span style="color:#ef4444;">${esc(e.message)}</span>`; }
  });
  root().querySelectorAll('[data-feat-del]').forEach(b => b.addEventListener('click', async () => {
    if (!confirm('Delete this feature?')) return;
    try { await api('DELETE', '/api/admin/email/features/' + b.getAttribute('data-feat-del')); await loadFeatures(); render(); }
    catch (e) { alert(e.message); }
  }));
}

async function loadFeatures() {
  const r = await api('GET', '/api/admin/email/features');
  features = r.data || [];
  githubConfigured = !!r.githubConfigured;
}

// ---- History ----------------------------------------------------------------

function historyView(campaigns) {
  const rows = (campaigns || []).map(c => {
    const openRate = c.sentCount ? Math.round((c.openCount / c.sentCount) * 100) : 0;
    const statusColor = { sent: '#10b981', sending: '#f59e0b', queued: '#64748b', scheduled: '#3b82f6', failed: '#ef4444', canceled: '#64748b' }[c.status] || '#64748b';
    const recBadge = c.recurrence ? ` <span style="font-size:10px;color:#8b5cf6;">(${esc(c.recurrence)} newsletter)</span>`
      : (c.parentId ? ' <span style="font-size:10px;color:#64748b;">(issue)</span>' : '');
    const schedLine = (c.status === 'scheduled' && c.scheduledAt)
      ? `<div style="font-size:10px;color:var(--text-dim);">${c.recurrence ? 'next ' : ''}${esc(new Date(c.scheduledAt).toLocaleString())}</div>` : '';
    const cancelBtn = (c.status === 'scheduled')
      ? `<button class="btn btn-sm" data-cancel="${c.id}" style="color:#ef4444;"><i class="fas fa-ban"></i> Cancel</button>` : '';
    return `<tr>
      <td style="white-space:nowrap;">${esc((c.createdAt || '').slice(0, 16).replace('T', ' '))}</td>
      <td>${esc(c.subject)}${recBadge}</td>
      <td>${esc(c.segment)}</td>
      <td><span style="color:${statusColor};">${esc(c.status)}</span>${schedLine}</td>
      <td>${c.sentCount}/${c.totalRecipients}${c.failCount ? ` <span style="color:#ef4444;">(${c.failCount} failed)</span>` : ''}</td>
      <td>${c.openCount} (${openRate}%)</td>
      <td style="white-space:nowrap;">${cancelBtn}</td>
    </tr>`;
  }).join('');
  return `
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
    <h3 style="margin:0;">Campaign history</h3>
    <button class="btn btn-sm" id="em-hist-refresh"><i class="fas fa-sync-alt"></i> Refresh</button>
  </div>
  <div class="admin-table-wrap"><table class="admin-table">
    <thead><tr><th>Created (UTC)</th><th>Subject</th><th>Segment</th><th>Status</th><th>Sent</th><th>Opens</th><th></th></tr></thead>
    <tbody>${rows || '<tr><td colspan="7" class="admin-empty">No campaigns yet.</td></tr>'}</tbody>
  </table></div>`;
}

let histTimer = null;
async function renderHistory() {
  let campaigns = [];
  try { campaigns = (await api('GET', '/api/admin/email/campaigns')).data || []; } catch (_) {}
  root().innerHTML = subnav() + historyView(campaigns);
  wireSubnav();
  document.getElementById('em-hist-refresh')?.addEventListener('click', renderHistory);
  root().querySelectorAll('[data-cancel]').forEach(b => b.addEventListener('click', async () => {
    if (!confirm('Cancel this scheduled campaign? It will not send.')) return;
    try { await api('POST', '/api/admin/email/campaigns/' + b.getAttribute('data-cancel') + '/cancel'); renderHistory(); }
    catch (e) { alert(e.message); }
  }));
  // Auto-refresh while anything is still sending.
  if (histTimer) clearInterval(histTimer);
  if (campaigns.some(c => c.status === 'sending' || c.status === 'queued')) {
    histTimer = setInterval(() => { if (area === 'history') renderHistory(); else clearInterval(histTimer); }, 4000);
  }
}

// ---- shell ------------------------------------------------------------------

function wireSubnav() {
  root().querySelectorAll('.email-subtab').forEach(b =>
    b.addEventListener('click', () => { area = b.getAttribute('data-area'); render(); }));
}

function render() {
  const el = root();
  if (!el) return;
  if (area === 'history') { renderHistory(); return; }
  el.innerHTML = subnav() + (area === 'templates' ? templatesView() : area === 'features' ? featuresView() : composeView());
  wireSubnav();
  if (area === 'compose') wireCompose();
  if (area === 'templates') wireTemplates();
  if (area === 'features') wireFeatures();
}

async function loadTemplates() { templates = (await api('GET', '/api/admin/email/templates')).data || []; }
async function loadSegments() { segments = (await api('GET', '/api/admin/email/segments')).data || []; }

// Distinct verticals from the public plugin catalog - these become the Domain options for
// per-domain newsletter issues and feature tagging. Static file, so a plain fetch (no auth).
async function loadDomains() {
  try {
    const res = await fetch('/pages/catalog.json', { cache: 'no-cache' });
    const cat = await res.json();
    const set = new Set();
    (Array.isArray(cat) ? cat : []).forEach(it => {
      const v = it && it.vertical ? String(it.vertical).trim() : '';
      // Exclude non-content catalog verticals (tiers/modules/add-ons) from the domain list.
      if (v && !['Subscription Tier', 'Research Engine', 'Add-on Product'].includes(v)) set.add(v);
    });
    domains = Array.from(set).sort((a, b) => a.localeCompare(b));
  } catch (_) { domains = []; }
}

let loaded = false;
async function open() {
  const el = root();
  if (!el) return;
  if (getUser()?.role !== 'admin') { el.innerHTML = '<p style="color:var(--text-dim);">Admin only.</p>'; return; }
  if (!loaded) {
    el.innerHTML = '<p style="color:var(--text-dim);"><i class="fas fa-spinner fa-spin"></i> Loading studio...</p>';
    try { await Promise.all([loadTemplates(), loadSegments(), loadFeatures(), loadDomains()]); loaded = true; }
    catch (e) {
      const is404 = /:\s*404$/.test(e.message);
      el.innerHTML = `<p style="color:#ef4444;">Could not load the email studio: ${esc(e.message)}.</p>` +
        (is404 ? '<p style="color:var(--text-dim);font-size:12px;">The email backend (GdbsWeb.Api.dll, v2.62.0) is not deployed yet - the API routes return 404. Deploy the DLL via the app_offline cycle and reload.</p>' : '');
      return;
    }
  }
  render();
}

export function initEmailStudio() {
  // Lazy: build the studio when the Campaigns subtab is first opened.
  const tab = document.querySelector('.atab[data-atab="campaigns"]');
  if (tab) tab.addEventListener('click', () => setTimeout(open, 40));
}
